import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminApi, useIsBootstrapMode } from '@/hooks/useAdminApi';
import toast from 'react-hot-toast';
import {
  Cloud,
  Check,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Shield,
  Users,
  AlertCircle,
  Copy,
  X,
  BookOpen,
  ExternalLink,
  Info,
  CheckCircle2,
  Upload,
} from 'lucide-react';
import { clsx } from 'clsx';

interface IdPGroup {
  id: string;
  name: string;
  email?: string;
  description: string | null;
}

interface RoleMappings {
  admin_group_ids: string[];
  leadership_group_ids: string[];
  product_team_group_ids: string[];
  user_group_ids: string[];
}

// Role definitions for mapping
const roleDefinitions = [
  { key: 'admin_group_ids', label: 'Admin', description: 'Full administrative access' },
  { key: 'leadership_group_ids', label: 'Leadership', description: 'Leadership team access' },
  { key: 'product_team_group_ids', label: 'Product Team', description: 'Product management team' },
  { key: 'user_group_ids', label: 'User', description: 'Standard user access' },
];

export function AdminIdentity() {
  const queryClient = useQueryClient();
  const apiClient = useAdminApi();
  const isBootstrapMode = useIsBootstrapMode();
  const [expandedSsoProvider, setExpandedSsoProvider] = useState<string | null>(null);
  const [ssoConfig, setSsoConfig] = useState<Record<string, any>>({});
  const [fetchedGroups, setFetchedGroups] = useState<IdPGroup[]>([]);
  const [roleMappings, setRoleMappings] = useState<RoleMappings>({
    admin_group_ids: [],
    leadership_group_ids: [],
    product_team_group_ids: [],
    user_group_ids: [],
  });
  const [isFetchingGroups, setIsFetchingGroups] = useState(false);
  const [showEntraGuide, setShowEntraGuide] = useState(true);
  const [showGoogleGuide, setShowGoogleGuide] = useState(true);

  const { data: integrationsData } = useQuery({
    queryKey: ['integrations', isBootstrapMode],
    queryFn: () => apiClient.getIntegrations(),
  });

  const { data: ssoStatus } = useQuery({
    queryKey: ['sso-status', isBootstrapMode],
    queryFn: () => apiClient.getSsoStatus(),
  });

  // Ensure integrations is always an array (defensive check)
  const integrations = Array.isArray(integrationsData) ? integrationsData : [];

  // Ensure a value is an array (defensive helper)
  const ensureArray = (value: unknown): string[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value) return value.split(',');
    return [];
  };

  // Load role mappings when a provider is expanded
  useEffect(() => {
    if (expandedSsoProvider) {
      apiClient.getRoleMappings(expandedSsoProvider).then((mappings) => {
        // Ensure all values are arrays (defensive)
        setRoleMappings({
          admin_group_ids: ensureArray(mappings.admin_group_ids),
          leadership_group_ids: ensureArray(mappings.leadership_group_ids),
          product_team_group_ids: ensureArray(mappings.product_team_group_ids),
          user_group_ids: ensureArray(mappings.user_group_ids),
        });
      }).catch(() => {
        setRoleMappings({
          admin_group_ids: [],
          leadership_group_ids: [],
          product_team_group_ids: [],
          user_group_ids: [],
        });
      });
    }
  }, [expandedSsoProvider, apiClient]);

  const configureSsoMutation = useMutation({
    mutationFn: ({ provider, config }: { provider: string; config: Record<string, any> }) => {
      if (provider === 'microsoft_entra') {
        return apiClient.configureMsEntra({
          tenantId: config.tenantId,
          clientId: config.clientId,
          clientSecret: config.clientSecret,
          samlMetadataUrl: config.samlMetadataUrl,
        });
      } else {
        return apiClient.configureGoogleWorkspace({
          clientId: config.clientId,
          clientSecret: config.clientSecret,
          domain: config.domain,
          samlMetadataXml: config.samlMetadataXml,
          serviceAccountJson: config.serviceAccountJson,
          adminEmail: config.adminEmail,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sso-status'] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success('SSO configuration saved');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to save SSO configuration');
    },
  });

  const activateSsoMutation = useMutation({
    mutationFn: (provider: string) => apiClient.activateSso(provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sso-status'] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success('SSO activated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to activate SSO');
    },
  });

  const deactivateSsoMutation = useMutation({
    mutationFn: () => apiClient.deactivateSso(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sso-status'] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success('SSO deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to deactivate SSO');
    },
  });

  const updateRoleMappingsMutation = useMutation({
    mutationFn: ({ provider, mappings }: { provider: string; mappings: RoleMappings }) =>
      apiClient.updateRoleMappings(provider, mappings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sso-status'] });
      toast.success('Role mappings updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update role mappings');
    },
  });

  const getIntegrationStatus = (type: string) => {
    return integrations.find((i) => i.type === type);
  };

  // Convert snake_case keys to camelCase for frontend form fields
  const snakeToCamel = (config: Record<string, any>): Record<string, any> => {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(config)) {
      // Convert snake_case to camelCase
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      result[camelKey] = value;
    }
    return result;
  };

  const handleSsoToggle = (provider: string) => {
    if (expandedSsoProvider === provider) {
      setExpandedSsoProvider(null);
      setSsoConfig({});
      setFetchedGroups([]);
    } else {
      const existing = getIntegrationStatus(provider);
      setExpandedSsoProvider(provider);
      // Convert snake_case keys from backend to camelCase for form fields
      const config = snakeToCamel(existing?.config || {});
      setSsoConfig(config);
      setFetchedGroups([]);

      // Auto-fetch groups if provider is configured
      if (provider === 'microsoft_entra' && config.tenantId && config.clientId) {
        // Trigger auto-fetch after state is set
        setTimeout(() => autoFetchGroups(provider, config), 100);
      } else if (provider === 'google_workspace' && config.domain && config.adminEmail) {
        setTimeout(() => autoFetchGroups(provider, config), 100);
      }
    }
  };

  const autoFetchGroups = async (provider: string, config: Record<string, any>) => {
    setIsFetchingGroups(true);
    try {
      let result;
      if (provider === 'microsoft_entra') {
        result = await apiClient.fetchEntraGroups({
          tenantId: config.tenantId,
          clientId: config.clientId,
          clientSecret: config.clientSecret || '',
        });
      } else {
        result = await apiClient.fetchGoogleGroups({
          serviceAccountJson: config.serviceAccountJson || '',
          domain: config.domain,
          adminEmail: config.adminEmail || '',
        });
      }
      setFetchedGroups(result.groups);
    } catch (error: any) {
      // Silently fail for auto-fetch - user can manually fetch if needed
      console.log('Auto-fetch groups failed:', error.response?.data?.detail || error.message);
    }
    setIsFetchingGroups(false);
  };

  const handleFetchGroups = async () => {
    if (!expandedSsoProvider) return;

    setIsFetchingGroups(true);
    try {
      let result;
      if (expandedSsoProvider === 'microsoft_entra') {
        // Send the request - backend will use stored secret if not provided or masked
        result = await apiClient.fetchEntraGroups({
          tenantId: ssoConfig.tenantId,
          clientId: ssoConfig.clientId,
          clientSecret: ssoConfig.clientSecret || '',
        });
      } else {
        // Send the request - backend will use stored secret if not provided or masked
        result = await apiClient.fetchGoogleGroups({
          serviceAccountJson: ssoConfig.serviceAccountJson || '',
          domain: ssoConfig.domain,
          adminEmail: ssoConfig.adminEmail || '',
        });
      }
      setFetchedGroups(result.groups);
      toast.success(`Found ${result.groups.length} groups`);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to fetch groups');
    }
    setIsFetchingGroups(false);
  };

  const handleGroupSelect = (roleKey: keyof RoleMappings, groupId: string, selected: boolean) => {
    setRoleMappings((prev) => ({
      ...prev,
      [roleKey]: selected
        ? [...prev[roleKey], groupId]
        : prev[roleKey].filter((id) => id !== groupId),
    }));
  };

  const handleSaveRoleMappings = () => {
    if (!expandedSsoProvider) return;
    updateRoleMappingsMutation.mutate({
      provider: expandedSsoProvider,
      mappings: roleMappings,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Identity</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Configure identity providers and single sign-on settings
        </p>
      </div>

      {/* SSO Configuration Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Single Sign-On (SSO)
          </h2>
        </div>

        {/* Active provider indicator */}
        {ssoStatus?.active_provider && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="text-green-800 dark:text-green-300 font-medium">
                  SSO Active: {ssoStatus.active_provider === 'microsoft_entra' ? 'Microsoft Entra ID' : 'Google Workspace'}
                </span>
              </div>
              <button
                onClick={() => deactivateSsoMutation.mutate()}
                disabled={deactivateSsoMutation.isPending}
                className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-md hover:bg-red-200 dark:hover:bg-red-900/70 disabled:opacity-50"
              >
                {deactivateSsoMutation.isPending ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        )}

        {/* Microsoft Entra ID */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => handleSsoToggle('microsoft_entra')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                <Cloud className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Microsoft Entra ID
                  </span>
                  {ssoStatus?.active_provider === 'microsoft_entra' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                      <Check className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  )}
                  {ssoStatus?.providers?.microsoft_entra?.configured && ssoStatus?.active_provider !== 'microsoft_entra' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300">
                      Configured
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Configure SAML SSO with Microsoft Entra ID (Azure AD)
                </p>
              </div>
            </div>
            {expandedSsoProvider === 'microsoft_entra' ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {expandedSsoProvider === 'microsoft_entra' && (
            <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-700">
              <div className="pt-4 space-y-6">
                {/* Setup Guide */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowEntraGuide(!showEntraGuide)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-blue-100/50 dark:hover:bg-blue-900/30 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <span className="font-medium text-blue-900 dark:text-blue-100">
                        Setup Guide: Configure Microsoft Entra ID
                      </span>
                    </div>
                    {showEntraGuide ? (
                      <ChevronUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    )}
                  </button>

                  {showEntraGuide && (
                    <div className="px-4 pb-4 space-y-4">
                      {/* Step 1 */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                            1
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              Create an Enterprise Application in Azure Portal
                            </h4>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1">Azure Portal <ExternalLink className="h-3 w-3" /></a></li>
                              <li>Navigate to <strong>Microsoft Entra ID</strong> → <strong>Enterprise applications</strong></li>
                              <li>Click <strong>+ New application</strong> → <strong>Create your own application</strong></li>
                              <li>Name it (e.g., "Product Portal") and select <strong>"Integrate any other application you don't find in the gallery (Non-gallery)"</strong></li>
                              <li>Click <strong>Create</strong></li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Step 2 */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                            2
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              Configure SAML Single Sign-On
                            </h4>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>In your new application, go to <strong>Single sign-on</strong> in the left menu</li>
                              <li>Select <strong>SAML</strong> as the sign-on method</li>
                              <li>In <strong>Basic SAML Configuration</strong>, click <strong>Edit</strong> and enter:
                                <div className="mt-2 ml-4 space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500 dark:text-gray-400 min-w-[140px]">Identifier (Entity ID):</span>
                                    <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs font-mono">
                                      {ssoStatus?.cognito_entity_id || 'Loading...'}
                                    </code>
                                    {ssoStatus?.cognito_entity_id && (
                                      <button
                                        onClick={() => copyToClipboard(ssoStatus.cognito_entity_id)}
                                        className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                        title="Copy"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500 dark:text-gray-400 min-w-[140px]">Reply URL (ACS URL):</span>
                                    <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs font-mono">
                                      {ssoStatus?.cognito_acs_url || 'Loading...'}
                                    </code>
                                    {ssoStatus?.cognito_acs_url && (
                                      <button
                                        onClick={() => copyToClipboard(ssoStatus.cognito_acs_url)}
                                        className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                        title="Copy"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </li>
                              <li>Click <strong>Save</strong></li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Step 3 */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                            3
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              Configure Attributes & Claims
                            </h4>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>In the SAML configuration, find <strong>Attributes & Claims</strong> and click <strong>Edit</strong></li>
                              <li>Ensure these claims are configured (add if missing):
                                <div className="mt-2 ml-4">
                                  <table className="text-xs w-full">
                                    <thead>
                                      <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <th className="text-left py-1 pr-4">Claim Name</th>
                                        <th className="text-left py-1">Source Attribute</th>
                                      </tr>
                                    </thead>
                                    <tbody className="font-mono">
                                      <tr><td className="py-1 pr-4">emailaddress</td><td>user.mail</td></tr>
                                      <tr><td className="py-1 pr-4">givenname</td><td>user.givenname</td></tr>
                                      <tr><td className="py-1 pr-4">surname</td><td>user.surname</td></tr>
                                      <tr><td className="py-1 pr-4">name</td><td>user.displayname</td></tr>
                                    </tbody>
                                  </table>
                                </div>
                              </li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Step 4 */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                            4
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              Copy Configuration Values
                            </h4>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>From your Entra application's <strong>Overview</strong> page, copy:
                                <ul className="ml-4 mt-1 list-disc list-inside">
                                  <li><strong>Application (client) ID</strong> → paste in "Client ID" below</li>
                                  <li><strong>Directory (tenant) ID</strong> → paste in "Tenant ID" below</li>
                                </ul>
                              </li>
                              <li>From the SAML configuration page, copy the <strong>App Federation Metadata Url</strong> → paste in "SAML Metadata URL" below</li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Step 5 - Optional */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-400 text-white flex items-center justify-center text-sm font-bold">
                            5
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                              Create Client Secret for Group Sync
                              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(Optional)</span>
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                              Required only if you want to sync groups for role mapping.
                            </p>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>Go to <strong>App registrations</strong> (not Enterprise applications)</li>
                              <li>Find and click your application</li>
                              <li>Go to <strong>Certificates & secrets</strong> → <strong>Client secrets</strong> → <strong>New client secret</strong></li>
                              <li>Copy the secret value immediately (it won't be shown again)</li>
                              <li>Go to <strong>API permissions</strong> → <strong>Add a permission</strong> → <strong>Microsoft Graph</strong> → <strong>Application permissions</strong></li>
                              <li>Add: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">Group.Read.All</code> and <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">User.Read.All</code></li>
                              <li>Click <strong>Grant admin consent</strong></li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Step 6 */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                            6
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              Assign Users & Groups
                            </h4>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>Back in <strong>Enterprise applications</strong>, select your app</li>
                              <li>Go to <strong>Users and groups</strong> → <strong>Add user/group</strong></li>
                              <li>Assign the users or groups that should have access to this portal</li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Completion note */}
                      <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-green-800 dark:text-green-200">
                          After completing these steps, fill in the configuration below, click <strong>Save Configuration</strong>, then <strong>Activate</strong> to enable SSO.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Configuration Form */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <Info className="h-4 w-4 text-gray-500" />
                    Configuration
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tenant ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={ssoConfig.tenantId || ''}
                        onChange={(e) => setSsoConfig({ ...ssoConfig, tenantId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Directory (tenant) ID from Azure Portal
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Client ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={ssoConfig.clientId || ''}
                        onChange={(e) => setSsoConfig({ ...ssoConfig, clientId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Application (client) ID from Azure Portal
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Client Secret
                      <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">(Optional - for group sync)</span>
                    </label>
                    <input
                      type="password"
                      value={ssoConfig.clientSecret || ''}
                      onChange={(e) => setSsoConfig({ ...ssoConfig, clientSecret: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter client secret for group sync"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Required only if you want to fetch groups for role mapping
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      SAML Metadata URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      value={ssoConfig.samlMetadataUrl || ''}
                      onChange={(e) => setSsoConfig({ ...ssoConfig, samlMetadataUrl: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="https://login.microsoftonline.com/{tenant}/federationmetadata/2007-06/federationmetadata.xml"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      App Federation Metadata URL from the SAML configuration page
                    </p>
                  </div>
                </div>

                {/* Role Mappings Section */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        Role Mappings
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                    </div>
                    <button
                      onClick={handleFetchGroups}
                      disabled={isFetchingGroups || !ssoConfig.tenantId || !ssoConfig.clientId}
                      className="inline-flex items-center px-3 py-1.5 text-sm bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-md hover:bg-primary-200 dark:hover:bg-primary-900/70 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className={clsx('h-4 w-4 mr-2', isFetchingGroups && 'animate-spin')} />
                      {isFetchingGroups ? 'Fetching...' : 'Fetch Groups'}
                    </button>
                  </div>

                  {fetchedGroups.length === 0 ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 rounded-md p-4 text-center">
                      <AlertCircle className="h-5 w-5 mx-auto mb-2" />
                      <p>Click "Fetch Groups" to load groups from Microsoft Entra ID.</p>
                      <p className="text-xs mt-1">A Client Secret must be saved in your configuration to fetch groups.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {roleDefinitions.map((role) => (
                        <div key={role.key} className="bg-gray-50 dark:bg-gray-900/50 rounded-md p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {role.label}
                              </span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {role.description}
                              </p>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {roleMappings[role.key as keyof RoleMappings].length} selected
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {roleMappings[role.key as keyof RoleMappings].map((groupId) => {
                              const group = fetchedGroups.find((g) => g.id === groupId);
                              return (
                                <span
                                  key={groupId}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 rounded text-xs"
                                >
                                  {group?.name || groupId}
                                  <button
                                    onClick={() => handleGroupSelect(role.key as keyof RoleMappings, groupId, false)}
                                    className="hover:text-red-600 dark:hover:text-red-400"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                          <select
                            className="mt-2 w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                handleGroupSelect(role.key as keyof RoleMappings, e.target.value, true);
                              }
                            }}
                          >
                            <option value="">Add a group...</option>
                            {fetchedGroups
                              .filter((g) => !roleMappings[role.key as keyof RoleMappings].includes(g.id))
                              .map((group) => (
                                <option key={group.id} value={group.id}>
                                  {group.name}
                                </option>
                              ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {fetchedGroups.length > 0 && (
                    <button
                      onClick={handleSaveRoleMappings}
                      disabled={updateRoleMappingsMutation.isPending}
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      {updateRoleMappingsMutation.isPending ? 'Saving...' : 'Save Role Mappings'}
                    </button>
                  )}
                  <button
                    onClick={() => configureSsoMutation.mutate({ provider: 'microsoft_entra', config: ssoConfig })}
                    disabled={configureSsoMutation.isPending || !ssoConfig.tenantId || !ssoConfig.clientId}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                  >
                    {configureSsoMutation.isPending ? 'Saving...' : 'Save Configuration'}
                  </button>
                  {ssoStatus?.providers?.microsoft_entra?.configured && ssoStatus?.active_provider !== 'microsoft_entra' && (
                    <button
                      onClick={() => activateSsoMutation.mutate('microsoft_entra')}
                      disabled={activateSsoMutation.isPending}
                      className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                    >
                      {activateSsoMutation.isPending ? 'Activating...' : 'Activate'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Google Workspace */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => handleSsoToggle('google_workspace')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-teal-100 dark:bg-teal-900/50 rounded-lg">
                <Cloud className="h-6 w-6 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Google Workspace
                  </span>
                  {ssoStatus?.active_provider === 'google_workspace' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                      <Check className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  )}
                  {ssoStatus?.providers?.google_workspace?.configured && ssoStatus?.active_provider !== 'google_workspace' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300">
                      Configured
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Configure SAML SSO with Google Workspace
                </p>
              </div>
            </div>
            {expandedSsoProvider === 'google_workspace' ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {expandedSsoProvider === 'google_workspace' && (
            <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-700">
              <div className="pt-4 space-y-6">
                {/* Setup Guide */}
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border border-teal-200 dark:border-teal-800 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowGoogleGuide(!showGoogleGuide)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-teal-100/50 dark:hover:bg-teal-900/30 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                      <span className="font-medium text-teal-900 dark:text-teal-100">
                        Setup Guide: Configure Google Workspace SSO
                      </span>
                    </div>
                    {showGoogleGuide ? (
                      <ChevronUp className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                    )}
                  </button>

                  {showGoogleGuide && (
                    <div className="px-4 pb-4 space-y-4">
                      {/* Step 1 */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-teal-100 dark:border-teal-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                            1
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              Create a SAML App in Google Admin Console
                            </h4>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>Go to <a href="https://admin.google.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 dark:text-teal-400 hover:underline inline-flex items-center gap-1">Google Admin Console <ExternalLink className="h-3 w-3" /></a></li>
                              <li>Navigate to <strong>Apps</strong> → <strong>Web and mobile apps</strong></li>
                              <li>Click <strong>Add app</strong> → <strong>Add custom SAML app</strong></li>
                              <li>Enter an app name (e.g., "Product Portal") and optionally upload an icon</li>
                              <li>Click <strong>Continue</strong></li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Step 2 */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-teal-100 dark:border-teal-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                            2
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              Download SAML Metadata
                            </h4>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>On the "Google Identity Provider details" page, click <strong>Download Metadata</strong> to get the XML file</li>
                              <li>Save the file — you will paste or upload its contents in the configuration below</li>
                              <li>Click <strong>Continue</strong></li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Step 3 */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-teal-100 dark:border-teal-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                            3
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              Configure Service Provider Details
                            </h4>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>On the "Service provider details" page, enter:
                                <div className="mt-2 ml-4 space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">ACS URL:</span>
                                    <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs font-mono">
                                      {ssoStatus?.cognito_acs_url || 'Loading...'}
                                    </code>
                                    {ssoStatus?.cognito_acs_url && (
                                      <button
                                        onClick={() => copyToClipboard(ssoStatus.cognito_acs_url)}
                                        className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                        title="Copy"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">Entity ID:</span>
                                    <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs font-mono">
                                      {ssoStatus?.cognito_entity_id || 'Loading...'}
                                    </code>
                                    {ssoStatus?.cognito_entity_id && (
                                      <button
                                        onClick={() => copyToClipboard(ssoStatus.cognito_entity_id)}
                                        className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                        title="Copy"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </li>
                              <li>Set <strong>Name ID format</strong> to <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">EMAIL</code></li>
                              <li>Set <strong>Name ID</strong> to <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">Basic Information &gt; Primary email</code></li>
                              <li>Click <strong>Continue</strong></li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Step 4 */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-teal-100 dark:border-teal-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                            4
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              Configure Attribute Mapping
                            </h4>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>On the "Attribute mapping" page, add these mappings:
                                <div className="mt-2 ml-4">
                                  <table className="text-xs w-full">
                                    <thead>
                                      <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <th className="text-left py-1 pr-4">Google Directory Attribute</th>
                                        <th className="text-left py-1">App Attribute</th>
                                      </tr>
                                    </thead>
                                    <tbody className="font-mono">
                                      <tr><td className="py-1 pr-4">Primary email</td><td>email</td></tr>
                                      <tr><td className="py-1 pr-4">First name</td><td>firstName</td></tr>
                                      <tr><td className="py-1 pr-4">Last name</td><td>lastName</td></tr>
                                    </tbody>
                                  </table>
                                </div>
                              </li>
                              <li>Click <strong>Finish</strong></li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Step 5 */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-teal-100 dark:border-teal-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                            5
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              Enable the App for Users
                            </h4>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>After creating the app, click on it to open settings</li>
                              <li>In the <strong>User access</strong> section, click to expand</li>
                              <li>Select <strong>ON for everyone</strong> or choose specific organizational units</li>
                              <li>Click <strong>Save</strong></li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Step 6 - Optional */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-teal-100 dark:border-teal-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-400 text-white flex items-center justify-center text-sm font-bold">
                            6
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                              Create Service Account for Group Sync
                              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(Optional)</span>
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                              Required only if you want to sync groups for role mapping.
                            </p>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 dark:text-teal-400 hover:underline inline-flex items-center gap-1">Google Cloud Console <ExternalLink className="h-3 w-3" /></a></li>
                              <li>Create a new project or select an existing one</li>
                              <li>Go to <strong>IAM & Admin</strong> → <strong>Service Accounts</strong> → <strong>Create Service Account</strong></li>
                              <li>Enter a name (e.g., "Product Portal Group Sync"), click <strong>Create and continue</strong>, skip the optional role and access grants, and click <strong>Done</strong></li>
                              <li>Click on the new service account → <strong>Keys</strong> tab → <strong>Add key</strong> → <strong>Create new key</strong> → select <strong>JSON</strong> → <strong>Create</strong></li>
                              <li>Save the downloaded JSON key file — you'll paste its contents below</li>
                              <li>Go to <strong>APIs & Services</strong> → <strong>Library</strong>, search for <strong>Admin SDK API</strong> and click <strong>Enable</strong></li>
                              <li>In <a href="https://admin.google.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 dark:text-teal-400 hover:underline inline-flex items-center gap-1">Google Admin Console <ExternalLink className="h-3 w-3" /></a>, go to <strong>Security</strong> → <strong>Access and data control</strong> → <strong>API controls</strong> → <strong>Manage Domain Wide Delegation</strong></li>
                              <li>Click <strong>Add new</strong>, enter the service account <strong>Client ID</strong> (from the JSON key file's <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">client_id</code> field) and add the scope:
                                <ul className="ml-4 mt-1 list-disc list-inside text-xs font-mono">
                                  <li>https://www.googleapis.com/auth/admin.directory.group.readonly</li>
                                </ul>
                              </li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Step 7 */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-teal-100 dark:border-teal-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                            7
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              Enter Configuration Below
                            </h4>
                            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                              <li>Enter your Google Workspace <strong>domain</strong> (e.g., yourcompany.com)</li>
                              <li>Paste or upload the <strong>SAML Metadata XML</strong> downloaded in Step 2</li>
                              <li>If using group sync, paste the service account JSON key contents</li>
                              <li>Enter an admin email address for the service account to impersonate</li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {/* Completion note */}
                      <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-green-800 dark:text-green-200">
                          After completing these steps, fill in the configuration below, click <strong>Save Configuration</strong>, then <strong>Activate</strong> to enable SSO.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Configuration Form */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <Info className="h-4 w-4 text-gray-500" />
                    Configuration
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Domain <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={ssoConfig.domain || ''}
                        onChange={(e) => setSsoConfig({ ...ssoConfig, domain: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="yourcompany.com"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Your Google Workspace domain
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        SAML Metadata XML <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={ssoConfig.samlMetadataXml || ''}
                        onChange={(e) => setSsoConfig({ ...ssoConfig, samlMetadataXml: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
                        rows={6}
                        placeholder="Paste the contents of the downloaded metadata XML file here..."
                      />
                      <div className="mt-1 flex items-center justify-between">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Download the metadata XML from Google Admin Console and paste its contents above
                        </p>
                        <label className="inline-flex items-center px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer">
                          <Upload className="h-3 w-3 mr-1" />
                          Upload XML file
                          <input
                            type="file"
                            accept=".xml,text/xml,application/xml"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  const content = event.target?.result as string;
                                  setSsoConfig({ ...ssoConfig, samlMetadataXml: content });
                                };
                                reader.readAsText(file);
                              }
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Group Sync Section */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                    Group Sync Configuration
                    <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(Optional)</span>
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    To fetch groups from Google Workspace for role mapping, provide a service account with domain-wide delegation.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Service Account JSON Key
                      </label>
                      {ssoConfig.serviceAccountJson === '********' ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 px-3 py-2 border border-green-300 dark:border-green-700 rounded-md bg-green-50 dark:bg-green-900/20">
                            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                            <span className="text-sm text-green-800 dark:text-green-300">Service account key configured</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSsoConfig({ ...ssoConfig, serviceAccountJson: '' })}
                            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                          >
                            Replace with new key
                          </button>
                        </div>
                      ) : (
                        <>
                          <textarea
                            value={ssoConfig.serviceAccountJson || ''}
                            onChange={(e) => setSsoConfig({ ...ssoConfig, serviceAccountJson: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
                            rows={4}
                            placeholder='Paste the entire contents of the JSON key file here...'
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            The JSON key downloaded from Google Cloud Console
                          </p>
                        </>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Admin Email (for impersonation)
                      </label>
                      <input
                        type="email"
                        value={ssoConfig.adminEmail || ''}
                        onChange={(e) => setSsoConfig({ ...ssoConfig, adminEmail: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="admin@yourcompany.com"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        A Google Workspace admin email for the service account to impersonate
                      </p>
                    </div>
                  </div>
                </div>

                {/* Role Mappings Section */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        Role Mappings
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                    </div>
                    <button
                      onClick={handleFetchGroups}
                      disabled={isFetchingGroups || !ssoConfig.domain || !ssoConfig.adminEmail}
                      className="inline-flex items-center px-3 py-1.5 text-sm bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-md hover:bg-primary-200 dark:hover:bg-primary-900/70 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className={clsx('h-4 w-4 mr-2', isFetchingGroups && 'animate-spin')} />
                      {isFetchingGroups ? 'Fetching...' : 'Fetch Groups'}
                    </button>
                  </div>

                  {fetchedGroups.length === 0 ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 rounded-md p-4 text-center">
                      <AlertCircle className="h-5 w-5 mx-auto mb-2" />
                      <p>Click "Fetch Groups" to load groups from Google Workspace.</p>
                      <p className="text-xs mt-1">Service Account JSON must be saved in your configuration to fetch groups.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {roleDefinitions.map((role) => (
                        <div key={role.key} className="bg-gray-50 dark:bg-gray-900/50 rounded-md p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {role.label}
                              </span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {role.description}
                              </p>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {roleMappings[role.key as keyof RoleMappings].length} selected
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {roleMappings[role.key as keyof RoleMappings].map((groupId) => {
                              const group = fetchedGroups.find((g) => (g.email?.split('@')[0] || g.id) === groupId);
                              return (
                                <span
                                  key={groupId}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 rounded text-xs"
                                >
                                  {group?.name || groupId}
                                  <button
                                    onClick={() => handleGroupSelect(role.key as keyof RoleMappings, groupId, false)}
                                    className="hover:text-red-600 dark:hover:text-red-400"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                          <select
                            className="mt-2 w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                handleGroupSelect(role.key as keyof RoleMappings, e.target.value, true);
                              }
                            }}
                          >
                            <option value="">Add a group...</option>
                            {fetchedGroups
                              .filter((g) => !roleMappings[role.key as keyof RoleMappings].includes(g.email?.split('@')[0] || g.id))
                              .map((group) => (
                                <option key={group.id} value={group.email?.split('@')[0] || group.id}>
                                  {group.name} ({group.email})
                                </option>
                              ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {fetchedGroups.length > 0 && (
                    <button
                      onClick={handleSaveRoleMappings}
                      disabled={updateRoleMappingsMutation.isPending}
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      {updateRoleMappingsMutation.isPending ? 'Saving...' : 'Save Role Mappings'}
                    </button>
                  )}
                  <button
                    onClick={() => configureSsoMutation.mutate({ provider: 'google_workspace', config: ssoConfig })}
                    disabled={configureSsoMutation.isPending || !ssoConfig.domain || !ssoConfig.samlMetadataXml}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                  >
                    {configureSsoMutation.isPending ? 'Saving...' : 'Save Configuration'}
                  </button>
                  {ssoStatus?.providers?.google_workspace?.configured && ssoStatus?.active_provider !== 'google_workspace' && (
                    <button
                      onClick={() => activateSsoMutation.mutate('google_workspace')}
                      disabled={activateSsoMutation.isPending}
                      className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                    >
                      {activateSsoMutation.isPending ? 'Activating...' : 'Activate'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
